import type { FilterBarValue, FilterInlineValue, SuiDateRange } from '@semeris/ui';

import { FilterValue } from '../components/filter-by-input/models/filter-by-input.model';
import { FilterPeriod } from '../components/filter-period-dropdown/filter-period-dropdown.component';
import { ComplexFilterResult } from '../models/filter/legacy-filter.model';

/**
 * Conversion seam between the sui filter value shape used in memory (stores, `<sui-filter-bar>`)
 * and the legacy shape still used on the wire — GraphQL queries and the persisted user-profile
 * filters (`positionFilters`, `activeFilter`, `excludeFilter`).
 *
 * Conversion is shape-driven, so no filter definition is needed:
 * - `string[]`                → unchanged
 * - `{include, exclude}`      ↔ `{includes, excludes}`
 * - `{start, end}`            ↔ `FilterPeriod` (sui carries no `relativeId`, so it is dropped
 *                                one way and left undefined the other)
 *
 * Relative periods do not survive the round trip: sui emits an already-resolved range, so a
 * saved "last 30 days" comes back as the absolute dates it resolved to. Restoring durable
 * relative periods needs the persisted shape to carry the preset id — tracked as the backend
 * follow-up, see docs/audits/filter-shape-sweep-audit.md.
 */

const isInline = (value: unknown): value is FilterInlineValue =>
  !!value && typeof value === 'object' && 'include' in value && 'exclude' in value;

const isComplexResult = (value: unknown): value is ComplexFilterResult =>
  !!value && typeof value === 'object' && 'includes' in value && 'excludes' in value;

const isRange = (value: unknown): value is { start: string | Date; end: string | Date } =>
  !!value && typeof value === 'object' && !Array.isArray(value) && 'start' in value && 'end' in value;

const asDate = (value: string | Date): Date => (value instanceof Date ? value : new Date(value));

/** sui `FilterBarValue` → legacy `FilterValue`, for GraphQL args and persisted profile filters. */
export function toLegacyFilterValue(bar: FilterBarValue | null | undefined): FilterValue {
  const legacy: FilterValue = {};

  for (const [key, value] of Object.entries(bar ?? {})) {
    if (Array.isArray(value)) {
      legacy[key] = [...value];
    } else if (isInline(value)) {
      legacy[key] = { includes: value.include ?? [], excludes: value.exclude ?? [] } satisfies ComplexFilterResult;
    } else if (isRange(value)) {
      legacy[key] = { start: asDate(value.start), end: asDate(value.end) } as FilterPeriod;
    }
  }

  return legacy;
}

/** Legacy `FilterValue` → sui `FilterBarValue`, for values read back off the wire. */
export function toBarFilterValue(legacy: FilterValue | null | undefined): FilterBarValue {
  const bar: FilterBarValue = {};

  for (const [key, value] of Object.entries(legacy ?? {})) {
    if (Array.isArray(value)) {
      bar[key] = [...value];
    } else if (isComplexResult(value)) {
      bar[key] = { include: value.includes ?? [], exclude: value.excludes ?? [] } satisfies FilterInlineValue;
    } else if (isRange(value)) {
      bar[key] = { start: asDate(value.start), end: asDate(value.end) } satisfies SuiDateRange;
    }
  }

  return bar;
}
